import { useState, useEffect, useMemo, useCallback } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { GraduationCap, RefreshCw, ChevronDown, Users, TrendingUp } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { COACHING_INSIGHTS_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import dayjs from "dayjs";

// ── minimal markdown rendering for model-written notes ───────────────────────
// Headings, bold, bullets, numbered lists and rules — no external dependency.

const inline = (text) =>
    String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((chunk, i) => {
        if (chunk.startsWith("**") && chunk.endsWith("**")) {
            return <strong key={i} className="font-semibold text-gray-900 dark:text-white">{chunk.slice(2, -2)}</strong>;
        }
        if (chunk.startsWith("`") && chunk.endsWith("`")) {
            return <code key={i} className="px-1 rounded bg-gray-100 dark:bg-gray-700 text-[11px]">{chunk.slice(1, -1)}</code>;
        }
        return <span key={i}>{chunk}</span>;
    });

const Markdown = ({ md }) => {
    const blocks = useMemo(() => {
        const out = [];
        (md || "").split("\n").forEach(raw => {
            const line = raw.trim();
            if (!line) return;
            if (/^-{3,}$/.test(line)) { out.push({ t: "rule" }); return; }
            const h = line.match(/^(#{1,6})\s+(.*)$/);
            if (h) { out.push({ t: "h", level: h[1].length, text: h[2] }); return; }
            const ol = line.match(/^(\d+)[.)]\s+(.*)$/);
            if (ol) { out.push({ t: "ol", n: ol[1], text: ol[2] }); return; }
            if (/^[-*]\s+/.test(line)) { out.push({ t: "li", text: line.replace(/^[-*]\s+/, "") }); return; }
            out.push({ t: "p", text: line });
        });
        return out;
    }, [md]);

    return (
        <div className="space-y-1.5">
            {blocks.map((b, i) => {
                if (b.t === "rule") return <hr key={i} className="border-gray-200 dark:border-gray-700 my-2" />;
                if (b.t === "h") return (
                    <p key={i} className={cn("font-semibold text-gray-900 dark:text-white",
                        b.level <= 2 ? "text-sm mt-2" : "text-xs uppercase tracking-wide text-gray-600 dark:text-gray-300 mt-2")}>
                        {inline(b.text)}
                    </p>
                );
                if (b.t === "li") return (
                    <div key={i} className="flex gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <span className="text-indigo-400 mt-0.5">•</span><span>{inline(b.text)}</span>
                    </div>
                );
                if (b.t === "ol") return (
                    <div key={i} className="flex gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <span className="text-indigo-400 tabular-nums">{b.n}.</span><span>{inline(b.text)}</span>
                    </div>
                );
                return <p key={i} className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{inline(b.text)}</p>;
            })}
        </div>
    );
};

// ── the measured numbers behind a note, so it can be checked ────────────────

const MetricRow = ({ label, value, suffix }) => (
    <div className="flex items-baseline justify-between gap-2 text-[11px]">
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        <span className="font-medium text-gray-800 dark:text-gray-200 tabular-nums">
            {value === null || value === undefined ? "—" : `${value}${suffix || ""}`}
        </span>
    </div>
);

const RepMetrics = ({ m }) => {
    if (!m) return null;
    const won = m.on_won_deals || {};
    const lost = m.on_lost_deals || {};
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="space-y-1">
                <MetricRow label="Won" value={m.wins} />
                <MetricRow label="Lost" value={m.losses} />
                <MetricRow label="Win rate" value={m.win_rate_pct} suffix="%" />
                <MetricRow label="Disqualified" value={m.disqualified_leads} />
            </div>
            <div className="space-y-1">
                <MetricRow label="Touches per win" value={won.avg_touches} />
                <MetricRow label="Touches per loss" value={lost.avg_touches} />
                <MetricRow label="Days to close" value={won.avg_days_to_close} />
                <MetricRow label="Days to first contact" value={won.avg_days_to_first_contact} />
            </div>
            <div className="space-y-1">
                <MetricRow label="Demo show rate" value={m.demo_show_rate_pct} suffix="%" />
                <MetricRow label="Demos booked" value={m.demos_booked_all_time} />
                <MetricRow label="Avg note length" value={m.avg_note_length_chars} suffix=" chars" />
                <MetricRow label="Overdue follow-ups" value={m.open_leads_with_overdue_followup} />
            </div>
        </div>
    );
};

const Coaching = () => {
    const { fetch } = useApiMethods();

    const [team,    setTeam]    = useState(null);
    const [reps,    setReps]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [openId,  setOpenId]  = useState(null);
    const [showMetrics, setShowMetrics] = usePersistedState("coaching_show_metrics", true);

    const load = useCallback(() => {
        setLoading(true);
        fetch(COACHING_INSIGHTS_URL)
            .then(res => {
                if (res?.status === "success") {
                    setTeam(res.team || null);
                    setReps(Array.isArray(res.reps) ? res.reps : []);
                } else {
                    ToastMsg("error", res?.message || "Failed to load coaching insights");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load coaching insights"))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    // Weakest win rate first — that's where coaching pays off most
    const ordered = useMemo(() => [...reps].sort((a, b) => {
        const av = a.metrics_json?.win_rate_pct ?? 999;
        const bv = b.metrics_json?.win_rate_pct ?? 999;
        return av - bv;
    }), [reps]);

    const nothing = !team && !reps.length;

    return (
        <ContainerCard>
            <div className="flex-shrink-0">
                <div className="flex items-center justify-between mb-4 px-1 gap-3 flex-wrap">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <GraduationCap className="h-4 w-4 text-indigo-500" />
                            Win / Loss Coaching
                        </h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            What the reps who win actually do differently — measured from the activity log, then interpreted
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                            <input type="checkbox" checked={showMetrics}
                                onChange={e => setShowMetrics(e.target.checked)}
                                className="rounded border-gray-300 dark:border-gray-600" />
                            Show the numbers
                        </label>
                        <Button onClick={load} size="sm" variant="outline"
                            className="flex items-center gap-1 text-xs" disabled={loading}>
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                        </Button>
                    </div>
                </div>
            </div>

            {loading && nothing ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading coaching insights…</p>
                    </div>
                </div>
            ) : nothing ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md px-6">
                        <GraduationCap className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No coaching notes yet</p>
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            Generate them with: manage.py analyze_win_loss --tenant &lt;slug&gt;
                        </p>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-0 overflow-auto space-y-4 pr-0.5">
                    {team && (
                        <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/15 p-4">
                            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                <p className="text-xs font-semibold text-indigo-900 dark:text-indigo-200 flex items-center gap-1.5">
                                    <Users className="h-3.5 w-3.5" /> Team briefing
                                </p>
                                <span className="text-[10px] text-indigo-700/70 dark:text-indigo-300/70">
                                    {team.period_start && team.period_end &&
                                        `${dayjs(team.period_start).format("MMM YYYY")} – ${dayjs(team.period_end).format("MMM YYYY")}`}
                                    {team.generated_at && ` · generated ${dayjs(team.generated_at).format("DD MMM")}`}
                                </span>
                            </div>
                            <Markdown md={team.summary_md} />
                            {team.metrics?.top_performers?.length > 0 && (
                                <p className="mt-3 pt-2 border-t border-indigo-200/60 dark:border-indigo-800/60 text-[11px] text-indigo-800 dark:text-indigo-300 flex items-center gap-1.5">
                                    <TrendingUp className="h-3 w-3" />
                                    Benchmarked against {team.metrics.top_performers.join(" and ")}
                                </p>
                            )}
                        </div>
                    )}

                    {ordered.map(rep => {
                        const isOpen = openId === rep.id;
                        const m = rep.metrics_json || {};
                        return (
                            <div key={rep.id}
                                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                                <button onClick={() => setOpenId(isOpen ? null : rep.id)}
                                    className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                    <span className="flex items-center gap-2 min-w-0">
                                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                            {rep.user_name || `Rep ${rep.user_id}`}
                                        </span>
                                        <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">
                                            {m.wins ?? 0} won · {m.losses ?? 0} lost
                                            {m.win_rate_pct != null && ` · ${m.win_rate_pct}% win rate`}
                                        </span>
                                    </span>
                                    <ChevronDown className={cn("h-4 w-4 text-gray-400 transition-transform flex-shrink-0",
                                        isOpen && "rotate-180")} />
                                </button>
                                {isOpen && (
                                    <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700">
                                        <Markdown md={rep.summary_md} />
                                        {showMetrics && <RepMetrics m={m} />}
                                        {rep.generated_at && (
                                            <p className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">
                                                Generated {dayjs(rep.generated_at).format("DD MMM YYYY, hh:mm A")}
                                                {rep.model_used ? ` · ${rep.model_used}` : ""}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </ContainerCard>
    );
};

export default Coaching;
